import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List, Tooltip } from "@mui/material";
import RectangleIcon from "@mui/icons-material/Rectangle";
import moment from "moment";
import { useNavigate } from "react-router-dom";

// Interface for the props of this component
interface NotebooksRequestTableProps {
    notebooks: any[]; // Expects the ProcessedNotebook structure from the parent
}

const NotebooksRequestTable: React.FC<NotebooksRequestTableProps> = ({
    notebooks,
}: NotebooksRequestTableProps) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    // Group statuses for styling
    const pendingStatuses = ['pending', 'submitted', 'provisioning'];
    const activeStatuses = ['active', 'approved', 'success'];
    const rejectedStatuses = ['rejected', 'failed'];

    return (
        <List sx={{ bgcolor: "#fafafa" }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        <Grid item xs={2}>
                            <Typography fontSize="12px">NOTEBOOK NAME</Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                            <Typography fontSize="12px">CLOUD PROVIDER</Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                            <Typography fontSize="12px">APPLICATION</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px">OWNER</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px">REQUESTED ON</Typography>
                        </Grid>
                        <Grid textAlign="start" item xs={2}>
                            <Typography fontSize="12px">ACTIONED BY/ REASON</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={1}>
                            <Typography fontSize="12px">ACTIONED ON</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </ListItem>
            {notebooks.map((notebook: any, index: number) => (
                <ListItem
                    key={notebook.id}
                    sx={{
                        border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
                        mt: 1,
                        cursor: "pointer",
                        bgcolor: activeStatuses.includes(notebook.status)
                                ? "#FFFFFF"
                                : rejectedStatuses.includes(notebook.status)
                                ? "#F0443810"
                                : "#FDB02210", // Default to pending color
                        borderRadius: "8px",
                    }}
                    onClick={() => {
                        // Navigate to a new notebook request details page
                        navigate(`/aisolutions/requests/notebook/${notebook.id}`);
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => {
                        setCardHoverIndex(null);
                    }}
                >
                    <Box width="100%" sx={{}}>
                        <Grid container direction="row" alignItems="center" justifyContent="space-between">
                            <Grid item xs={2}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <RectangleIcon
                                        sx={{
                                            color: activeStatuses.includes(notebook.status)
                                                    ? "#12B76A"
                                                    : rejectedStatuses.includes(notebook.status)
                                                    ? "#F04438"
                                                    : "#FDB022",
                                            bgcolor: activeStatuses.includes(notebook.status)
                                                    ? "#12B76A"
                                                    : rejectedStatuses.includes(notebook.status)
                                                    ? "#F04438"
                                                    : "#FDB022",
                                            width: "4px",
                                            marginRight: "8px",
                                        }}
                                    />
                                    <Typography fontSize="14px" fontFamily="Gilroy-Medium">
                                        {notebook.notebookName}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1.5}>
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                    {notebook.cloudProvider || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={1.5}>
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                    {notebook.applicationName || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Avatar
                                        src={notebook.notebookOwnerAvatar} // Will be undefined, showing fallback
                                        alt={notebook.notebookOwnerName}
                                        sx={{ marginRight: "8px", height: 30, width: 30 }}
                                    >
                                     {/* Fallback to initials if no src */}
                                     {notebook.notebookOwnerName ? notebook.notebookOwnerName.charAt(0).toUpperCase() : ''}
                                    </Avatar>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {notebook.notebookOwnerName || 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {notebook.createdAt ? moment(notebook.createdAt).format("DD/MM/YYYY | hh:mm A") : 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                {notebook.status !== "pending" && (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {notebook.notebookApproverName && (
                                        <Avatar
                                            alt={notebook.notebookApproverName}
                                            src={notebook.notebookApproverAvatar} // <-- ADD THIS LINE
                                            sx={{ marginRight: "8px", height: 30, width: 30 }}
                                        >
                                            {notebook.notebookApproverName.charAt(0).toUpperCase()}
                                        </Avatar>
                                        )}

                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" mr={1}>
                                            {notebook.notebookApproverName || 'N/A'}
                                        </Typography>
                                        {rejectedStatuses.includes(notebook.status) && notebook.reasonForRejection && (
                                            <Tooltip title={`Reason: ${notebook.reasonForRejection}`}>
                                                <img src="/assets/info.svg" alt="info" />
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={1}>
                                {notebook.actionedAt && !pendingStatuses.includes(notebook.status) && (
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                            {moment(notebook.actionedAt).format("DD/MM/YYYY | hh:mm A")}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default NotebooksRequestTable;



// import React from "react";
// import { Box, Typography, Avatar, Grid, ListItem, List, Tooltip } from "@mui/material";
// import RectangleIcon from "@mui/icons-material/Rectangle";
// import moment from "moment";
// import { useNavigate } from "react-router-dom";

// // Interface for the props of this component
// interface NotebooksRequestTableProps {
//     notebooks: any[]; // Expects the ProcessedNotebook structure from the parent
// }

// const NotebooksRequestTable: React.FC<NotebooksRequestTableProps> = ({
//     notebooks,
// }: NotebooksRequestTableProps) => {
//     const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
//     const navigate = useNavigate();

//     // Group statuses for styling
//     const pendingStatuses = ['pending', 'submitted', 'provisioning'];
//     const activeStatuses = ['active', 'approved', 'success'];
//     const rejectedStatuses = ['rejected', 'failed'];

//     return (
//         <List sx={{ bgcolor: "#fafafa" }}>
//             <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
//                 <Box width="100%">
//                     <Grid container direction="row" alignItems="center">
//                         <Grid item xs={2}>
//                             <Typography fontSize="12px">NOTEBOOK NAME</Typography>
//                         </Grid>
//                         <Grid item xs={2}>
//                             <Typography fontSize="12px">APPLICATION</Typography>
//                         </Grid>
//                         <Grid item textAlign="start" xs={2}>
//                             <Typography fontSize="12px">OWNER</Typography>
//                         </Grid>
//                         <Grid item textAlign="start" xs={2}>
//                             <Typography fontSize="12px">REQUESTED ON</Typography>
//                         </Grid>
//                         <Grid textAlign="start" item xs={2}>
//                             <Typography fontSize="12px">ACTIONED BY/ REASON</Typography>
//                         </Grid>
//                         <Grid item textAlign="start" xs={2}>
//                             <Typography fontSize="12px">ACTIONED ON</Typography>
//                         </Grid>
//                     </Grid>
//                 </Box>
//             </ListItem>
//             {notebooks.map((notebook: any, index: number) => (
//                 <ListItem
//                     key={notebook.id}
//                     sx={{
//                         border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
//                         mt: 1,
//                         cursor: "pointer",
//                         bgcolor: activeStatuses.includes(notebook.status)
//                                 ? "#FFFFFF"
//                                 : rejectedStatuses.includes(notebook.status)
//                                 ? "#F0443810"
//                                 : "#FDB02210", // Default to pending color
//                         borderRadius: "8px",
//                     }}
//                     onClick={() => {
//                         // Navigate to a new notebook request details page
//                         navigate(`/aisolutions/requests/notebook/${notebook.id}`);
//                     }}
//                     onMouseEnter={() => setCardHoverIndex(index)}
//                     onMouseLeave={() => {
//                         setCardHoverIndex(null);
//                     }}
//                 >
//                     <Box width="100%" sx={{}}>
//                         <Grid container direction="row" alignItems="center" justifyContent="space-between">
//                             <Grid item xs={2}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <RectangleIcon
//                                         sx={{
//                                             color: activeStatuses.includes(notebook.status)
//                                                     ? "#12B76A"
//                                                     : rejectedStatuses.includes(notebook.status)
//                                                     ? "#F04438"
//                                                     : "#FDB022",
//                                             bgcolor: activeStatuses.includes(notebook.status)
//                                                     ? "#12B76A"
//                                                     : rejectedStatuses.includes(notebook.status)
//                                                     ? "#F04438"
//                                                     : "#FDB022",
//                                             width: "4px",
//                                             marginRight: "8px",
//                                         }}
//                                     />
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Medium">
//                                         {notebook.notebookName}
//                                     </Typography>
//                                 </Box>
//                             </Grid>
//                             <Grid item xs={2}>
//                                 <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                                     {notebook.applicationName || 'N/A'}
//                                 </Typography>
//                             </Grid>
//                             <Grid item xs={2}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <Avatar
//                                         src={notebook.notebookOwnerAvatar} // Will be undefined, showing fallback
//                                         alt={notebook.notebookOwnerName}
//                                         sx={{ marginRight: "8px", height: 30, width: 30 }}
//                                     >
//                                      {/* Fallback to initials if no src */}
//                                      {notebook.notebookOwnerName ? notebook.notebookOwnerName.charAt(0).toUpperCase() : ''}
//                                     </Avatar>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                                         {notebook.notebookOwnerName || 'N/A'}
//                                     </Typography>
//                                 </Box>
//                             </Grid>
//                             <Grid item xs={2}>
//                                 <Box display="flex" alignItems="center" justifyContent="space-between">
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                                         {notebook.createdAt ? moment(notebook.createdAt).format("DD/MM/YYYY | hh:mm A") : 'N/A'}
//                                     </Typography>
//                                 </Box>
//                             </Grid>
//                             <Grid item xs={2}>
//                                 {notebook.status !== "pending" && (
//                                     <Box sx={{ display: "flex", alignItems: "center" }}>
//                                         {notebook.notebookApproverName && (
//                                           <Avatar
//                                               alt={notebook.notebookApproverName}
//                                               sx={{ marginRight: "8px", height: 30, width: 30 }}
//                                           >
//                                             {notebook.notebookApproverName.charAt(0).toUpperCase()}
//                                           </Avatar>
//                                         )}
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" mr={1}>
//                                             {notebook.notebookApproverName || 'N/A'}
//                                         </Typography>
//                                         {rejectedStatuses.includes(notebook.status) && notebook.reasonForRejection && (
//                                             <Tooltip title={`Reason: ${notebook.reasonForRejection}`}>
//                                                 <img src="/assets/info.svg" alt="info" />
//                                             </Tooltip>
//                                         )}
//                                     </Box>
//                                 )}
//                             </Grid>
//                             <Grid item xs={2}>
//                                 {notebook.actionedAt && !pendingStatuses.includes(notebook.status) && (
//                                     <Box display="flex" alignItems="center" justifyContent="space-between">
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                                             {moment(notebook.actionedAt).format("DD/MM/YYYY | hh:mm A")}
//                                         </Typography>
//                                     </Box>
//                                 )}
//                             </Grid>
//                         </Grid>
//                     </Box>
//                 </ListItem>
//             ))}
//         </List>
//     );
// };

// export default NotebooksRequestTable;




// import React from "react";
// import { Box, Typography, Avatar, Grid, ListItem, List, Tooltip } from "@mui/material";
// import RectangleIcon from "@mui/icons-material/Rectangle";
// import moment from "moment";
// import { useNavigate } from "react-router-dom";

// // Interface for the props of this component
// interface NotebooksRequestTableProps {
//     notebooks: any[]; // Expects the ProcessedNotebook structure from the parent
// }

// const NotebooksRequestTable: React.FC<NotebooksRequestTableProps> = ({
//     notebooks,
// }: NotebooksRequestTableProps) => {
//     const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
//     const navigate = useNavigate();

//     // Group statuses for styling
//     const pendingStatuses = ['pending', 'submitted', 'provisioning']; 
//     const activeStatuses = ['active', 'approved', 'success'];
//     const rejectedStatuses = ['rejected', 'failed'];
        
//     return (
//         <List sx={{ bgcolor: "#fafafa" }}>
//             <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
//                 <Box width="100%">
//                     <Grid container direction="row" alignItems="center">
//                         <Grid item xs={2}>
//                             <Typography fontSize="12px">NOTEBOOK NAME</Typography>
//                         </Grid>
//                         <Grid item textAlign="start" xs={3}>
//                             <Typography fontSize="12px">OWNER</Typography>
//                         </Grid>
//                         <Grid item textAlign="start" xs={2}>
//                             <Typography fontSize="12px">REQUESTED ON</Typography>
//                         </Grid>
//                         <Grid textAlign="start" item xs={3}>
//                             <Typography fontSize="12px">ACTIONED BY/ REASON</Typography>
//                         </Grid>
//                         <Grid item textAlign="start" xs={2}>
//                             <Typography fontSize="12px">ACTIONED ON</Typography>
//                         </Grid>
//                     </Grid>
//                 </Box>
//             </ListItem>
//             {notebooks.map((notebook: any, index: number) => (
//                 <ListItem
//                     key={notebook.id}
//                     sx={{
//                         border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
//                         mt: 1,
//                         cursor: "pointer",
//                         bgcolor: activeStatuses.includes(notebook.status)
//                                 ? "#FFFFFF"
//                                 : rejectedStatuses.includes(notebook.status)
//                                 ? "#F0443810"
//                                 : "#FDB02210", // Default to pending color
//                         borderRadius: "8px",
//                     }}
//                     onClick={() => {
//                         // Navigate to a new notebook request details page
//                         navigate(`/aisolutions/requests/notebook/${notebook.id}`);
//                     }}
//                     onMouseEnter={() => setCardHoverIndex(index)}
//                     onMouseLeave={() => {
//                         setCardHoverIndex(null);
//                     }}
//                 >
//                     <Box width="100%" sx={{}}>
//                         <Grid container direction="row" alignItems="center" justifyContent="space-between">
//                             <Grid item xs={2}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <RectangleIcon
//                                         sx={{
//                                             color: activeStatuses.includes(notebook.status)
//                                                     ? "#12B76A"
//                                                     : rejectedStatuses.includes(notebook.status)
//                                                     ? "#F04438"
//                                                     : "#FDB022",
//                                             bgcolor: activeStatuses.includes(notebook.status)
//                                                     ? "#12B76A"
//                                                     : rejectedStatuses.includes(notebook.status)
//                                                     ? "#F04438"
//                                                     : "#FDB022",
//                                             width: "4px",
//                                             marginRight: "8px",
//                                         }}
//                                     />
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Medium">
//                                         {notebook.notebookName}
//                                     </Typography>
//                                 </Box>
//                             </Grid>
//                             <Grid item xs={3}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <Avatar
//                                         src={notebook.notebookOwnerAvatar} // Will be undefined, showing fallback
//                                         alt={notebook.notebookOwnerName}
//                                         sx={{ marginRight: "8px", height: 30, width: 30 }}
//                                     >
//                                      {/* Fallback to initials if no src */}
//                                      {notebook.notebookOwnerName ? notebook.notebookOwnerName.charAt(0).toUpperCase() : ''}
//                                     </Avatar>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                                         {notebook.notebookOwnerName || 'N/A'}
//                                     </Typography>
//                                 </Box>
//                             </Grid>
//                             <Grid item xs={2}>
//                                 <Box display="flex" alignItems="center" justifyContent="space-between">
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                                         {notebook.createdAt ? moment(notebook.createdAt).format("DD/MM/YYYY | hh:mm A") : 'N/A'}
//                                     </Typography>
//                                 </Box>
//                             </Grid>
//                             <Grid item xs={3}>
//                                 {notebook.status !== "pending" && (
//                                     <Box sx={{ display: "flex", alignItems: "center" }}>
//                                         {notebook.notebookApproverName && (
//                                           <Avatar
//                                               alt={notebook.notebookApproverName}
//                                               sx={{ marginRight: "8px", height: 30, width: 30 }}
//                                           >
//                                             {notebook.notebookApproverName.charAt(0).toUpperCase()}
//                                           </Avatar>
//                                         )}
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" mr={1}>
//                                             {notebook.notebookApproverName || 'N/A'}
//                                         </Typography>
//                                         {rejectedStatuses.includes(notebook.status) && notebook.reasonForRejection && (
//                                             <Tooltip title={`Reason: ${notebook.reasonForRejection}`}>
//                                                 <img src="/assets/info.svg" alt="info" />
//                                             </Tooltip>
//                                         )}
//                                     </Box>
//                                 )}
//                             </Grid>
//                             <Grid item xs={2}>
//                                 {notebook.actionedAt && !pendingStatuses.includes(notebook.status) && (
//                                     <Box display="flex" alignItems="center" justifyContent="space-between">
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                                             {moment(notebook.actionedAt).format("DD/MM/YYYY | hh:mm A")}
//                                         </Typography>
//                                     </Box>
//                                 )}
//                             </Grid>
//                         </Grid>
//                     </Box>
//                 </ListItem>
//             ))}
//         </List>
//     );
// };

// export default NotebooksRequestTable;